/**
 * Company Contacts Tab Component
 * Displays and manages company contacts as mini-cards
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Grid,
  Paper,
  IconButton,
  Chip,
  Typography,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Contacts as ContactsIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import {
  CompanyContact,
  CompanyContactFormData,
  ContactType,
  CONTACT_TYPE_LABELS,
  formatPhone,
  removePhoneMask,
} from '../../../../types/company';
import { useListaPermitida } from '../../../../authorization/useListaPermitida';
import { companyService } from '../../../../services/companyService';
import { ContactFormModal } from '../modals';
import { ActionButton, ProfileSection } from '../../../../components/common';

interface CompanyContactsTabProps {
  companyId: string;
}

const CompanyContactsTab: React.FC<CompanyContactsTabProps> = ({
  companyId,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [activeOnly, setActiveOnly] = useState(true);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<CompanyContact | undefined>();
  const [modalError, setModalError] = useState<string | null>(null);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<CompanyContact | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Gerir contatos é permissão própria, e quem só lê a empresa não a tem: o cartão vinha com
  // alerta vermelho de erro para uma recusa que não é falha nenhuma.
  const carregarContatos = useCallback(
    () => companyService.getContacts(companyId, activeOnly),
    [companyId, activeOnly]
  );
  const lista = useListaPermitida<CompanyContact>(
    'company.manage-contacts',
    'Contatos',
    carregarContatos
  );
  const contacts = lista.itens;
  const loading = lista.carregando || !lista.resolvida;
  const error = lista.erro ?? null;
  const loadContacts = lista.recarregar;

  // Handle add contact
  const handleAdd = () => {
    setSelectedContact(undefined);
    setModalError(null);
    setModalOpen(true);
  };

  // Handle edit contact
  const handleEdit = (contact: CompanyContact) => {
    setSelectedContact(contact);
    setModalError(null);
    setModalOpen(true);
  };

  // Handle delete contact
  const handleDeleteClick = (contact: CompanyContact) => {
    setContactToDelete(contact);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!contactToDelete) return;

    setDeleteLoading(true);
    try {
      await companyService.deleteContact(companyId, contactToDelete.id.toString());
      enqueueSnackbar('Contato excluido com sucesso', { variant: 'success' });
      loadContacts();
    } catch (err: any) {
      enqueueSnackbar(err.message ?? 'Erro ao excluir contato', { variant: 'error' });
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setContactToDelete(null);
    }
  };

  // Handle toggle active
  const handleToggleActive = async (contact: CompanyContact) => {
    try {
      await companyService.updateContact(companyId, contact.id.toString(), {
        isActive: !contact.isActive,
      });
      enqueueSnackbar(
        contact.isActive ? 'Contato desativado' : 'Contato ativado',
        { variant: 'success' }
      );
      loadContacts();
    } catch (err: any) {
      enqueueSnackbar(err.message ?? 'Erro ao atualizar contato', { variant: 'error' });
    }
  };

  // Handle form submit
  const handleFormSubmit = async (data: CompanyContactFormData) => {
    setModalError(null);
    try {
      const payload = {
        contactType: data.contactType as ContactType,
        name: data.name,
        email: data.email ?? undefined,
        phone: data.phone ? removePhoneMask(data.phone) : undefined,
        isActive: true,
      };

      if (selectedContact) {
        await companyService.updateContact(
          companyId,
          selectedContact.id.toString(),
          payload
        );
        enqueueSnackbar('Contato atualizado com sucesso', { variant: 'success' });
      } else {
        await companyService.createContact(companyId, payload);
        enqueueSnackbar('Contato adicionado com sucesso', { variant: 'success' });
      }
      loadContacts();
    } catch (err: any) {
      setModalError(err.message ?? 'Erro ao salvar contato');
      throw err;
    }
  };

  return (
    <ProfileSection
      title="Contatos"
      icon={<ContactsIcon />}
      action={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Apenas ativos</Typography>}
          />
          <ActionButton
            acao="company.manage-contacts"
            rotulo="Adicionar contato"
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            Adicionar
          </ActionButton>
        </Box>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? null : contacts.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary" gutterBottom>
            {lista.bloqueio ?? 'Nenhum contato cadastrado'}
          </Typography>
          {lista.permitido && (
            <Button size="small" startIcon={<AddIcon />} onClick={handleAdd}>
              Adicionar Contato
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={2}>
          {contacts.map((contact) => (
            <Grid key={contact.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Chip
                  label={CONTACT_TYPE_LABELS[contact.contactType]}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ alignSelf: 'flex-start' }}
                />
                <Typography variant="subtitle2" sx={{ mt: 1 }} noWrap>
                  {contact.name}
                </Typography>
                {contact.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {contact.email}
                    </Typography>
                  </Box>
                )}
                {contact.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      {formatPhone(contact.phone)}
                    </Typography>
                  </Box>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 'auto',
                    pt: 1,
                  }}
                >
                  <Chip
                    label={contact.isActive ? 'Ativo' : 'Inativo'}
                    size="small"
                    color={contact.isActive ? 'success' : 'default'}
                    onClick={() => handleToggleActive(contact)}
                    sx={{ cursor: 'pointer' }}
                  />
                  <Box>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleEdit(contact)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(contact)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Form Modal */}
      <ContactFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleFormSubmit}
        contact={selectedContact}
        error={modalError}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Exclusao</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir o contato &quot;{contactToDelete?.name}&quot;?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
    </ProfileSection>
  );
};

export default CompanyContactsTab;
